import { type } from 'arktype';

export const timeTrackingPeriodQuerySchema = type({
  'timetrackingPersonId?': type('string').configure({
    description: 'Filter periods belonging to a specific time-tracking person.',
    examples: ['ttp_018ef4a2'],
  }),
}).configure({ description: 'Query parameters for listing time-tracking periods.' });

export type TimeTrackingPeriodQuery = typeof timeTrackingPeriodQuerySchema.t;

const workloadHoursPerDayField = type('number').configure({
  description: 'Contractual working hours per workday during this period (hours-mode). Mutually exclusive with workloadPercent.',
  examples: [8],
});

const workloadPercentField = type('number').configure({
  description: 'Workload as a percentage of the tenant full-time baseline (percent-mode). Mutually exclusive with workloadHoursPerDay.',
  examples: [100],
});

export const createTimeTrackingPeriodSchema = type({
  timetrackingPersonId: type('string').configure({
    description: 'ID of the time-tracking person this period belongs to.',
    examples: ['ttp_018ef4a2'],
  }),
  'workloadHoursPerDay?': workloadHoursPerDayField,
  'workloadPercent?': workloadPercentField,
  'workdayMask?': type('number').configure({
    description:
      'Bitmask of working weekdays (bit 0 = Monday … bit 6 = Sunday). Defaults to 31 (Monday–Friday). Determines which past days incur an expected-hours deficit.',
    examples: [31],
  }),
  'balanceAccrualStartDate?': type('string | null').configure({
    description:
      'Earliest date the expected-hours flexitime deficit may accrue (YYYY-MM-DD). `null` or omitted means accrue from the period start. Must not precede the period start.',
    examples: ['2026-06-01'],
  }),
  startDate: type('string').configure({
    description: 'Inclusive period start as ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-01-01'],
  }),
  'endDate?': type('string | null').configure({
    description: 'Inclusive period end as ISO 8601 date. `null` or omitted means the period is open-ended.',
    examples: ['2026-12-31'],
  }),
  'rrule?': type('string | null').configure({
    description: 'iCal RRULE string. `null` or omitted indicates a non-recurring period.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR'],
  }),
})
  .narrow((data, ctx) => {
    const hasHours = data.workloadHoursPerDay !== undefined;
    const hasPercent = data.workloadPercent !== undefined;
    if (hasHours && hasPercent) {
      return ctx.reject({ expected: 'exactly one of workloadHoursPerDay or workloadPercent', actual: 'both provided' });
    }
    if (!hasHours && !hasPercent) {
      return ctx.reject({ expected: 'exactly one of workloadHoursPerDay or workloadPercent', actual: 'neither provided' });
    }
    return true;
  })
  .configure({
    description:
      'Payload for creating a time-tracking period for a person. Exactly one of workloadHoursPerDay or workloadPercent must be provided.',
  });

export type CreateTimeTrackingPeriod = typeof createTimeTrackingPeriodSchema.infer & { rrule?: string | null | undefined };

export const updateTimeTrackingPeriodSchema = type({
  'workloadHoursPerDay?': workloadHoursPerDayField,
  'workloadPercent?': workloadPercentField,
  'workdayMask?': type('number').configure({
    description: 'Updated bitmask of working weekdays (bit 0 = Monday … bit 6 = Sunday).',
    examples: [31],
  }),
  'balanceAccrualStartDate?': type('string | null').configure({
    description: 'Updated earliest accrual date for the expected-hours deficit (YYYY-MM-DD), or `null` to accrue from the period start.',
    examples: ['2026-06-01'],
  }),
  'startDate?': type('string').configure({
    description: 'Updated inclusive period start (YYYY-MM-DD).',
    examples: ['2026-01-01'],
  }),
  'endDate?': type('string | null').configure({
    description: 'Updated inclusive period end. `null` reopens an end-dated period.',
    examples: ['2026-12-31'],
  }),
  'rrule?': type('string | null').configure({
    description: 'Updated iCal RRULE string. `null` clears recurrence.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR'],
  }),
})
  .narrow((data, ctx) => {
    const hasHours = data.workloadHoursPerDay !== undefined;
    const hasPercent = data.workloadPercent !== undefined;
    if (hasHours && hasPercent) {
      return ctx.reject({ expected: 'at most one of workloadHoursPerDay or workloadPercent', actual: 'both provided' });
    }
    return true;
  })
  .configure({
    description: 'Partial update for a time-tracking period. At most one of workloadHoursPerDay or workloadPercent may be provided.',
  });

export type UpdateTimeTrackingPeriod = typeof updateTimeTrackingPeriodSchema.infer & { rrule?: string | null | undefined };

export interface TimeTrackingPeriod {
  id: string;
  timetrackingPersonId: string;
  /** Hours-mode value. Null when period uses percent-mode. */
  workloadHoursPerDay: number | null;
  /** Percent-mode value. Null when period uses hours-mode. */
  workloadPercent: number | null;
  workdayMask: number;
  balanceAccrualStartDate: string | null;
  startDate: string;
  endDate: string | null;
  rrule: string | null;
}
